/**
 * The Control Center's Connectors section (tracker #336): the shard's SMTP,
 * Telegram Bot API and Twilio WhatsApp connectors — their purpose, audience,
 * lifecycle and credential status as the server states them, the three acts
 * a manager has (purpose and state, credential replacement, credential
 * removal), and every way the server refuses one.
 *
 * Nothing here names a credential's value, length or fragment: the server
 * returns none, and the words promise none. A provider name (Telegram,
 * Twilio, WhatsApp) and a protocol name (SMTP, TLS, STARTTLS) stand untranslated
 * in every locale, as brands and notation do.
 */
export const controlCenterConnectors = {
  "connectors.title": "Message delivery channels",
  'connectors.subtitle':
    'The email, Telegram and WhatsApp connectors this installation sends through: their purpose, state and credential status. Credentials are stored write-only and never shown.',
  'connectors.loading': 'Loading connectors',
  'connectors.loadFailed': 'Could not load connectors',
  'connectors.forbidden': 'You may not read connectors',
  'connectors.unavailable': 'Connectors are unavailable right now',
  'connectors.tableLabel': 'Connector inventory',
  'connectors.empty': 'No connectors',
  'connectors.emptyDetail': 'Connectors registered in this installation will appear here.',
  'connectors.noMatches': 'No connectors match these filters',
  'connectors.noMatchesDetail': 'Widen or clear the column filters to return to the full inventory.',
  'connectors.column.identity': 'Identity',
  'connectors.column.channel': 'Channel',
  'connectors.column.provider': 'Provider',
  'connectors.column.purpose': 'Purpose',
  'connectors.column.audience': 'Audience',
  'connectors.column.lifecycle': 'Lifecycle',
  'connectors.column.credentials': 'Credentials',
  'connectors.column.migration': 'Migration',
  'connectors.column.updated': 'Last change',
  'connectors.actions': 'Actions',
  'connectors.editNamed': 'Edit {name}',
  'connectors.openNamed': 'Open {name}',
  'connectors.unrecognized': '{value} (unrecognized)',
  'connectors.identity.unresolved': 'Identity not resolved',

  'connectors.channel.email': 'Email',
  'connectors.channel.telegram': 'Telegram',
  'connectors.channel.whatsapp': 'WhatsApp',
  'connectors.provider.smtp': 'SMTP',
  'connectors.provider.telegram_bot_api': 'Telegram Bot API',
  'connectors.provider.twilio_whatsapp': 'Twilio WhatsApp',
  'connectors.purpose.transactional_email': 'Transactional email',
  'connectors.purpose.staff_communications': 'Staff communications',
  'connectors.purpose.patient_communications': 'Patient communications',
  'connectors.purpose.legacy_mixed': 'Legacy: staff and patients',
  'connectors.purpose.migration_unresolved': 'Migration unresolved',
  'connectors.audience.staff': 'Staff',
  'connectors.audience.patient': 'Patients',
  'connectors.audience.none': 'Not set',
  'connectors.lifecycle.enabled': 'Enabled',
  'connectors.lifecycle.disabled': 'Disabled',
  'connectors.credentials.configured': 'Configured',
  'connectors.credentials.not_configured': 'Not configured',
  'connectors.credentials.unresolved': 'Unresolved',
  'connectors.migration.pending': 'Pending',
  'connectors.migration.key_unavailable': 'Key unavailable',
  'connectors.migration.invalid_envelope': 'Invalid envelope',
  'connectors.migration.identity_unresolved': 'Identity unresolved',
  'connectors.migration.ownership_unresolved': 'Ownership unresolved',
  'connectors.migration.legacy_purpose_unresolved': 'Legacy purpose unresolved',
  'connectors.migration.resolved': 'Resolved',
  'connectors.migration.cleared': 'Cleared',

  'connectors.detailLoading': 'Loading connector',
  'connectors.facts': 'Connector',
  'connectors.fact.channel': 'Channel',
  'connectors.fact.provider': 'Provider',
  'connectors.fact.purpose': 'Purpose',
  'connectors.fact.audience': 'Audience',
  'connectors.fact.lifecycle': 'Lifecycle',
  'connectors.fact.credentials': 'Credentials',
  'connectors.fact.migration': 'Migration',
  'connectors.fact.botUsername': 'Bot',
  'connectors.fact.botUserId': 'Bot ID',
  'connectors.fact.smtpHost': 'Host',
  'connectors.fact.smtpPort': 'Port',
  'connectors.fact.smtpServerName': 'TLS server name',
  'connectors.fact.smtpTlsMode': 'TLS mode',
  'connectors.fact.fromAddress': 'Sender address',
  'connectors.fact.fromName': 'Sender name',
  'connectors.fact.twilioAccountPreview': 'Account SID',
  'connectors.fact.whatsappFromPreview': 'WhatsApp sender',
  'connectors.fact.created': 'Created',
  'connectors.fact.updated': 'Last change',
  'connectors.readOnly': 'Read-only',
  'connectors.readOnlyNote': 'You may read connector status but not change it.',
  'connectors.secretsNote': 'Stored credentials are never shown. Leave them closed to keep them as they are.',
  'connectors.metadata.title': 'Purpose and state',
  'connectors.purpose': 'Purpose',
  'connectors.lifecycle': 'Lifecycle',
  'connectors.notConfiguredHint': 'Save credentials before enabling this connector.',
  'connectors.save': 'Save connector',
  'connectors.saving': 'Saving',
  'connectors.saved': 'Connector saved',
  'connectors.reload': 'Reload connector',
  'connectors.reloaded': 'Reloaded from the server. Review the changes and save again.',
  'connectors.reloadFailed': 'Could not reload the connector.',
  'connectors.unsavedIntent': 'Connector form \u2014 entries that were never saved',
  'connectors.credentials.title': 'Credentials',
  'connectors.credentials.replace': 'Replace credentials',
  'connectors.credentials.keep': 'Keep existing credentials',
  'connectors.credentials.rotate': 'Save new credentials',
  'connectors.credentials.rotating': 'Checking and saving',
  'connectors.credentials.rotated': 'Credentials replaced',
  'connectors.credentials.clear': 'Remove credentials',
  'connectors.credentials.clearing': 'Removing',
  'connectors.credentials.cleared': 'Credentials removed',
  'connectors.credentials.clearTitle': 'Remove the stored credentials?',
  'connectors.credentials.clearDescription':
    'The connector is disabled and its credentials are erased. Its identity, purpose and history are kept, and new credentials can be saved later.',
  'connectors.credentials.clearConfirm': 'Remove',
  'connectors.cancel': 'Cancel',
  'connectors.legacyNote':
    'This bot was migrated from the former clinic setting and still serves staff and patients together. Choose one purpose to resolve it; the other audience is then free for another bot.',
  'connectors.unresolvedNote':
    'This record preserves a Telegram bot whose credentials could not be migrated. Its purpose and credentials cannot be changed here; remove the credentials to retire it.',

  'connectors.createTitle': 'New connector',
  'connectors.create': 'Create connector',
  'connectors.creating': 'Checking and creating',
  'connectors.createNote': 'A new connector starts disabled. Enable it once its credentials are saved.',
  'connectors.providerKind': 'Provider',
  'connectors.field.smtpHost': 'SMTP host',
  'connectors.field.smtpPort': 'Port',
  'connectors.field.smtpServerName': 'TLS server name',
  'connectors.field.smtpTlsMode': 'TLS mode',
  'connectors.field.tls.starttls': 'STARTTLS',
  'connectors.field.tls.tls': 'TLS',
  'connectors.field.fromAddress': 'Sender address',
  'connectors.field.fromName': 'Sender name',
  'connectors.field.smtpUsername': 'Login',
  'connectors.field.smtpPassword': 'Password',
  'connectors.field.telegramBotToken': 'Bot token',
  'connectors.field.twilioAccountSid': 'Account SID',
  'connectors.field.whatsappFrom': 'WhatsApp sender',
  'connectors.field.whatsappFromHint': 'International format, starting with a plus sign.',
  'connectors.field.twilioAuthToken': 'Auth token',
  'connectors.problem.smtp': 'Fill in the host, port, TLS server name, sender address, login and password.',
  'connectors.problem.smtpPort': 'The port must be a whole number from 1 to 65535.',
  'connectors.problem.telegram': 'Enter the bot token.',
  'connectors.problem.twilio': 'Fill in the account SID, the WhatsApp sender and the auth token.',
  'connectors.problem.whatsappFrom': 'The WhatsApp sender must be an international number starting with a plus sign.',

  'connectors.error.revisionConflict':
    'Someone else changed this connector while the panel was open. Reload it to see the current revision; your entries are kept.',
  'connectors.error.duplicateIdentity': 'A connector with this identity already exists. Open the existing one instead.',
  'connectors.error.purposeConflict':
    'Another connector already serves this purpose and audience. Choose a different purpose, or reassign the other connector first.',
  'connectors.error.identityMismatch':
    'These credentials belong to a different bot or sender. Replacing credentials cannot change a connector’s identity; create a new connector instead.',
  'connectors.error.notConfigured': 'The connector has no credentials, so it cannot be enabled. Save credentials first.',
  'connectors.error.migrationUnresolved': 'This record’s migration is unresolved. Remove its credentials to retire it.',
  'connectors.error.invalid': 'The provider or the server rejected these fields. Review them and try again; your entries are kept.',
  'connectors.error.notFound': 'This connector no longer exists on the server.',
  'connectors.error.forbidden': 'You may not change connectors.',
  'connectors.error.unavailable': 'The server is unavailable. Your entries are kept.',
  'connectors.error.conflict': 'The server refused this change because of a conflict. Reload the connector and try again.',
  'connectors.error.unknown': 'The server returned an unexpected answer. Your entries are kept.',
} as const
